// public/v3/src/components/pain-no-scale-review-modal.jsx — SOFT-guard
// confirmation for G3002 when no validated pain scale is on file this month.
//
// The missing NRS is normally a hard CPM blocker. Proceeding is an explicit,
// warned operator decision; the caller audits it and threads the acknowledgement
// through generation or push. The note remains omit-if-absent and never claims
// a scale that is not stored in raw.pain.assessments[].

import React, { useEffect } from 'react';

function formatMonthLongFromKey(monthKey) {
  if (!monthKey || typeof monthKey !== 'string') return '';
  const m = monthKey.match(/^(\d{4})-(\d{2})/);
  if (!m) return '';
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo)) return '';
  const d = new Date(y, mo - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// mode: 'generate' | 'push' — only changes the action verb in the copy/button.
export function PainNoScaleReviewModal({ patient, monthStr, mode, onClose, onProceed, busy }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const raw = patient?._raw || patient || {};
  const name = ((raw.fname || '') + ' ' + (raw.lname || '')).trim() || patient?.name || 'this patient';
  const currentMonth = formatMonthLongFromKey(monthStr);
  const verb = mode === 'push' ? 'Push & Sign' : 'Generate';

  return (
    <div className="modal-backdrop" onClick={() => { if (!busy) onClose(); }}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`No validated pain scale for ${name}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <h3>No validated pain scale this month — {name}{currentMonth ? ` · ${currentMonth}` : ''}</h3>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0 }}>
            <b>No validated pain scale (NRS) is on file</b> for {name} this month.
            Chronic Pain Management (G3002) expects a validated pain scale during the service month.
          </p>
          <p>
            You can proceed anyway. If you do, the billing note will <b>not</b> claim a pain-scale
            assessment was documented, and this acknowledgement will be recorded in the audit log.
          </p>
          <p style={{ marginBottom: 0, color: 'var(--ink-3)', fontSize: 13 }}>
            Only proceed if the scale is documented elsewhere or the billing practitioner has a
            supported reason to bill without the structured NRS. Otherwise, cancel and record NRS on the Pain lens first.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn warn" onClick={() => { if (!busy) onProceed(); }} disabled={busy}>
            {busy ? 'Working…' : `Proceed without pain scale — ${verb}`}
          </button>
        </div>
      </div>
    </div>
  );
}
